import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getCurrentSweepstake, enterSweepstake, markSweepstakeVisualPlayed, CurrentSweepstakeResponse } from '../../services/sweepstakes';
import { useToast } from '@chakra-ui/react';
import { getImageUrl } from '../../utils/imageUtils';
import { useTranslation } from 'react-i18next';

const fmtDate = (iso?: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleString('cs-CZ');
};

const SweepstakeWidget: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [data, setData] = useState<CurrentSweepstakeResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [joining, setJoining] = useState<boolean>(false);
  const [playing, setPlaying] = useState<boolean>(false);
  const playedRef = useRef(false);
  const toast = useToast();

  const load = async () => {
    setLoading(true);
    try {
      const res = await getCurrentSweepstake();
      setData(res);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const s = data?.sweepstake;
  const prizes = data?.prizes || [];
  const winners = data?.winners || [];
  const state = data?.state || 'upcoming';
  const isLogged = !!user;
  const isAdmin = String((user as any)?.role || '').toLowerCase() === 'admin';
  const iWon = useMemo(() => {
    if (!isLogged || !winners?.length) return false;
    const myId = (user as any)?.id;
    return winners.some(w => w.user_id === myId);
  }, [winners, isLogged, user]);

  const hasEntered = !!data?.has_entered;
  const canEnter = !!data?.can_enter;

  useEffect(() => {
    // Autoplay visualization once for logged users within 3-day window, non-admins
    if (!s || !isLogged || !winners?.length) return;
    if (state !== 'finalized') return;
    if (data?.visual_played_at) return;
    if (playing || playedRef.current) return;
    if (isAdmin) return; // admin can trigger manually from admin page; avoid auto here
    setPlaying(true);
    playedRef.current = true;
    const t = setTimeout(async () => {
      try { await markSweepstakeVisualPlayed(s.id); } catch {}
      setPlaying(false);
      await load();
    }, 3000);
    return () => clearTimeout(t);
  }, [s, isLogged, winners, state, data?.visual_played_at, playing, isAdmin]);

  if (loading) return null;
  if (!s) return null;
  // Hide finalized widget for users who are not winners
  if ((data?.state || 'upcoming') === 'finalized' && !iWon) return null;

  const onJoin = async () => {
    if (!s) return;
    setJoining(true);
    try {
      await enterSweepstake(s.id);
      toast({ status: 'success', title: 'Úspěšně jste se přihlásili do soutěže' });
      await load();
    } catch (e: any) {
      const msg = e?.response?.data?.error || 'Nelze se přihlásit do soutěže';
      toast({ status: 'error', title: msg });
    } finally {
      setJoining(false);
    }
  };

  const doPlay = async () => {
    if (!s) return;
    setPlaying(true);
    const t = setTimeout(async () => {
      try { await markSweepstakeVisualPlayed(s.id); } catch {}
      setPlaying(false);
      await load();
    }, 2500);
    return () => clearTimeout(t);
  };

  return (
    <section data-element="sweepstakes" data-variant="default" style={{ marginTop: 16, marginBottom: 8 }}>
      <div className="card" style={{ maxWidth: 1200, margin: '0 auto', padding: 16 }}>
        {state === 'upcoming' && (
          <div>
            <div className="section-head" style={{ marginTop: 0 }}>
              <h3>Soutěž</h3>
              {s.rules_url && (<a href={getImageUrl(s.rules_url) || s.rules_url} className="see-all" target="_blank" rel="noreferrer noopener">Pravidla</a>)}
            </div>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
              {s.image_url && (
                // eslint-disable-next-line jsx-a11y/alt-text
                <img src={getImageUrl(s.image_url)} style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 8 }} />
              )}
              <div style={{ flex: 1, minWidth: 240 }}>
                <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 4 }}>{s.title}</div>
                {s.description && <div style={{ opacity: 0.8 }}>{s.description}</div>}
                <div style={{ marginTop: 8, fontSize: 14, opacity: 0.8 }}>Začíná: {fmtDate(s.start_at)} • Končí: {fmtDate(s.end_at)}</div>
              </div>
              {!isLogged ? (
                <a href="/login" className="btn" style={{ padding: '10px 16px' }}>{t('auth.login')}</a>
              ) : hasEntered ? (
                <span style={{ fontWeight: 600 }}>Už jste přihlášeni do soutěže ✓</span>
              ) : (
                <span style={{ fontSize: 14, opacity: 0.85 }}>Soutěž ještě nezačala. Vstup bude možný od {fmtDate(s.start_at)}.</span>
              )}
            </div>
          </div>
        )}

        {state === 'active' && (
          <div>
            <div className="section-head" style={{ marginTop: 0 }}>
              <h3>Soutěž</h3>
              {s.rules_url && (<a href={getImageUrl(s.rules_url) || s.rules_url} className="see-all" target="_blank" rel="noreferrer noopener">Pravidla</a>)}
            </div>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
              {s.image_url && (
                // eslint-disable-next-line jsx-a11y/alt-text
                <img src={getImageUrl(s.image_url)} style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 8 }} />
              )}
              <div style={{ flex: 1, minWidth: 240 }}>
                <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 4 }}>{s.title}</div>
                {s.description && <div style={{ opacity: 0.8 }}>{s.description}</div>}
                <div style={{ marginTop: 8, fontSize: 14, opacity: 0.8 }}>Konec: {fmtDate(s.end_at)}</div>
                <div style={{ marginTop: 6, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13, background: '#eef', color: '#223', padding: '2px 8px', borderRadius: 12 }}>Vstup: {(s as any).entry_cost_points ? `${(s as any).entry_cost_points} bodů` : 'zdarma'}</span>
                  {(s as any).max_entries_per_user > 1 && (
                    <span style={{ fontSize: 13, background: '#f3f3f3', color: '#333', padding: '2px 8px', borderRadius: 12 }}>max {(s as any).max_entries_per_user}×/osoba</span>
                  )}
                </div>
              </div>
              {!isLogged ? (
                <a href="/login" className="btn" style={{ padding: '10px 16px' }}>{t('auth.login')}</a>
              ) : hasEntered ? (
                <span style={{ fontWeight: 600 }}>Už jste přihlášeni do soutěže ✓</span>
              ) : canEnter ? (
                <button className="btn" onClick={onJoin} disabled={joining}>
                  {joining ? 'Přihlašuji…' : 'Přihlásit do soutěže'}
                </button>
              ) : (
                <span style={{ fontWeight: 600 }}>Vstup není dostupný</span>
              )}
            </div>
          </div>
        )}

        {state === 'finalized' && (
          <div>
            <div className="section-head" style={{ marginTop: 0 }}>
              <h3>Výherci soutěže</h3>
              {s.rules_url && (<a href={getImageUrl(s.rules_url)} className="see-all" target="_blank" rel="noreferrer noopener">Pravidla</a>)}
            </div>
            {winners.length === 0 ? (
              <div>Výherci budou vyhlášeni brzy.</div>
            ) : (
              <div>
                {/* Visualization */}
                {!data?.visual_played_at && isLogged && (
                  <div style={{ marginBottom: 12 }}>
                    {playing ? (
                      <div style={{ padding: 16, border: '1px dashed #999', borderRadius: 8, textAlign: 'center' }}>
                        <div style={{ fontWeight: 700, marginBottom: 6 }}>Losuji výherce…</div>
                        <div className="spinner" style={{ width: 24, height: 24, borderRadius: '50%', border: '3px solid #ccc', borderTopColor: '#333', margin: '0 auto', animation: 'spin 0.9s linear infinite' }} />
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <button className="btn" onClick={doPlay}>Spustit losování</button>
                        <span style={{ opacity: 0.8, fontSize: 14 }}>(animace pouze jednou na uživatele)</span>
                      </div>
                    )}
                  </div>
                )}
                {/* Winners list */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                  {winners.map((w) => (
                    <div key={w.id} className="card" style={{ padding: 12 }}>
                      <div style={{ fontWeight: 700 }}>{w.prize_name || 'Výhra'}</div>
                      <div style={{ fontSize: 14, opacity: 0.8 }}>Výherce: {user && w.user_id === (user as any).id ? 'Vy' : 'Vybraný uživatel'}</div>
                    </div>
                  ))}
                </div>
                {iWon && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ fontWeight: 800 }}>Vyhráli jste! Více informací najdete ve svém e-mailu.</div>
                    <div style={{ marginTop: 6 }}>
                      Pokud potřebujete pomoc, <a href={"/kontakt?subject=" + encodeURIComponent("Soutěž – výhra: " + (s.title || ''))} className="see-all">kontaktujte nás</a>.
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      <style>{`@keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}`}</style>
    </section>
  );
};

export default SweepstakeWidget;
